import { ConstraintMode } from 'types/ConstraintProfileTypes';

/**
 * The set of constraint paths that are user-saved per mode — the "Snapshot: Yes"
 * rows from the Vincoli spec sheets. Source of truth for:
 *
 *   - the `(modificato)` badge: is `workingDocument` different from the
 *     baseline (saved profile or predefiniti)?
 *   - the per-section red dots on profile cards: does the profile differ
 *     from the mode's predefiniti?
 *   - the save payload at Salva: what do we ship to the backend?
 *
 * Anything NOT in the mode's set is display-only / SSO-static / advisor-
 * curated / mode-irrelevant. The constraint-comparison logic ignores it
 * regardless of what value it carries in `workingDocument`, `predefiniti`,
 * or `profile.constraints`. That's the whole point of this file —
 * eliminate "is the field null or undefined or missing or empty-dict" guess-
 * work and replace it with one explicit list per mode.
 *
 * Each entry is a `group.field` path matching the Document shape.
 * Microcategoria advice is a single dict-valued field under
 * `microcategoria_advice.microcategory_weights`.
 *
 * Keep these lists in sync with `docs/.../constraints-fields-spec.md` (the
 * spreadsheet the user uses to author the rules).
 */

const COMMON_SAVABLE_PATHS = [
  // Indicators
  'indicator.final_score_min',
  'indicator.final_score_advice_min',
  'indicator.esg_rating_min',
  // Dividendi / YTM / Duration
  'dividend.dividend_yield_min',
  'dividend.dividend_yield_max',
  'dividend.ytm_min',
  'dividend.ytm_max',
  'dividend.duration_min',
  'dividend.duration_max',
  // Costi
  'cost.total_cost_min',
  'cost.total_cost_max',
  'cost.discount_min',
  'cost.discount_max',
  // Strumenti — instrument_type (5 types, min/max)
  'instrument_type.mutual_funds_min',
  'instrument_type.mutual_funds_max',
  'instrument_type.etf_min',
  'instrument_type.etf_max',
  'instrument_type.bonds_min',
  'instrument_type.bonds_max',
  'instrument_type.stocks_min',
  'instrument_type.stocks_max',
  'instrument_type.certificates_min',
  'instrument_type.certificates_max',
  // Strumenti — core/satellite
  'core_satellite.core_min',
  'core_satellite.core_max',
  'core_satellite.satellite_min',
  'core_satellite.satellite_max',
  // Top quality
  'top_quality.top_quality_percent_min',
  'top_quality.top_quality_percent_max',
  // Wrapper (builder-only, issue #63)
  'wrapper.wrapper_percent_min',
  'wrapper.wrapper_percent_max',
  // Macro Asset Allocation (6 classes, min/max)
  'macro_asset_allocation.equity_min',
  'macro_asset_allocation.equity_max',
  'macro_asset_allocation.high_risk_bonds_min',
  'macro_asset_allocation.high_risk_bonds_max',
  'macro_asset_allocation.low_medium_risk_bonds_min',
  'macro_asset_allocation.low_medium_risk_bonds_max',
  'macro_asset_allocation.money_market_min',
  'macro_asset_allocation.money_market_max',
  'macro_asset_allocation.alternative_commodity_min',
  'macro_asset_allocation.alternative_commodity_max',
  'macro_asset_allocation.other_min',
  'macro_asset_allocation.other_max',
  // Asset Allocation Implicita — macro.* (16 geo/sectoral, min/max)
  'macro.north_america_equities_min',
  'macro.north_america_equities_max',
  'macro.europe_equities_min',
  'macro.europe_equities_max',
  'macro.pacific_equities_min',
  'macro.pacific_equities_max',
  'macro.emerging_equities_min',
  'macro.emerging_equities_max',
  'macro.global_high_yield_bonds_min',
  'macro.global_high_yield_bonds_max',
  'macro.euro_high_yield_bonds_min',
  'macro.euro_high_yield_bonds_max',
  'macro.emerging_market_hard_currency_bonds_min',
  'macro.emerging_market_hard_currency_bonds_max',
  'macro.emerging_market_local_currency_bonds_min',
  'macro.emerging_market_local_currency_bonds_max',
  'macro.international_bonds_min',
  'macro.international_bonds_max',
  'macro.euro_corporate_bonds_min',
  'macro.euro_corporate_bonds_max',
  'macro.euro_government_bonds_min',
  'macro.euro_government_bonds_max',
  'macro.money_market_min',
  'macro.money_market_max',
  'macro.short_term_min',
  'macro.short_term_max',
  'macro.alternative_min',
  'macro.alternative_max',
  'macro.commodity_min',
  'macro.commodity_max',
  'macro.other_min',
  'macro.other_max',
  // Esposizione valutaria (4 currencies, min/max)
  'currency_exposure.euro_min',
  'currency_exposure.euro_max',
  'currency_exposure.us_dollar_min',
  'currency_exposure.us_dollar_max',
  'currency_exposure.emerging_markets_min',
  'currency_exposure.emerging_markets_max',
  'currency_exposure.other_min',
  'currency_exposure.other_max',
  // Microcategorie (server-fetched + advisor-edited)
  'microcategoria_advice.microcategory_weights',
];

const BUILDER_ONLY_SAVABLE_PATHS = [
  // Risk: builder lets the advisor pick the profile and tune target VaR
  // through the slider — risk_profile and target_var ARE snapshot fields here.
  'risk.financial_plan',
  'risk.risk_profile',
  'risk.target_var',
  // ESG (rebalancing hides this entire group)
  'esg.esg_preference',
  'esg.lettera_a',
  'esg.lettera_b',
  'esg.lettera_c',
  'esg.pai_e',
  'esg.pai_s',
  'esg.pai_g',
  // Strumenti — asset counts/weights and investment thresholds are
  // builder-only sliders (rebalancing inherits portfolio composition).
  'asset.asset_count_min',
  'asset.asset_count_max',
  'asset.asset_weight_min',
  'asset.asset_weight_max',
  'investment_threshold.investment_bonds_max',
  'investment_threshold.investment_funds_max',
];

const REBALANCING_ONLY_SAVABLE_PATHS = [
  // Risk: risk_profile is read-only from SSO (NOT saved), but VaR floor /
  // ceiling and align-to-plan ARE saved.
  'risk.financial_plan',
  'risk.target_var_min',
  'risk.target_var_max',
  'risk.align_to_plan',
  // Operazioni — rebalancing-only group
  'order.positive_value_min',
  'order.positive_value_max',
  'order.positive_amount_min',
  'order.assets_to_buy_min',
  'order.assets_to_buy_max',
  'order.assets_to_sell_min',
  'order.assets_to_sell_max',
];

export const SAVABLE_PATHS: Record<ConstraintMode, ReadonlySet<string>> = {
  builder: new Set<string>([...COMMON_SAVABLE_PATHS, ...BUILDER_ONLY_SAVABLE_PATHS]),
  rebalancing: new Set<string>([...COMMON_SAVABLE_PATHS, ...REBALANCING_ONLY_SAVABLE_PATHS]),
};

/** Decompose `'group.field'` into `[group, field]`. */
export function splitPath(path: string): [string, string] {
  const dot = path.indexOf('.');
  return [path.slice(0, dot), path.slice(dot + 1)];
}
